/**
 * Real-time Notifications Component
 * Shows notifications when admin makes changes
 */
import React, { useEffect, useState } from 'react';
import { useRealtimeNotifications } from '../hooks/useRealtimeSync';
import { Bell, X, CheckCircle, AlertCircle, Info } from 'lucide-react';

const RealtimeNotifications = () => {
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { notifications } = useRealtimeNotifications(user.id);
  const [visible, setVisible] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Count unread notifications with safety check
    if (Array.isArray(notifications)) {
      const unread = notifications.filter(n => n && !n.read).length;
      setUnreadCount(unread);
    }
  }, [notifications]);

  // Request notification permission on mount (only if supported)
  useEffect(() => {
    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
      Notification.requestPermission().catch((err) => {
        console.log('Notification permission denied:', err);
      });
    }
  }, []);

  const getIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'warning':
        return <AlertCircle className="w-5 h-5 text-yellow-400" />;
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Info className="w-5 h-5 text-cyan-400" />;
    }
  };

  return (
    <div className="fixed top-4 right-4 z-50">
      {/* Notification Bell */}
      <button
        onClick={() => setVisible(!visible)}
        className="relative p-3 bg-slate-800 border-2 border-cyan-500/50 rounded-full hover:bg-slate-700 transition-all"
      >
        <Bell className="w-5 h-5 text-cyan-400" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs flex items-center justify-center rounded-full">
            {unreadCount}
          </span>
        )}
      </button>

      {/* Notifications Panel */}
      {visible && (
        <div className="absolute top-16 right-0 w-96 max-h-96 overflow-y-auto bg-slate-900 border-2 border-cyan-500/50 rounded-lg shadow-2xl">
          <div className="p-4 border-b border-cyan-500/30 flex items-center justify-between">
            <h3 className="font-bold text-white">Notifications</h3>
            <button onClick={() => setVisible(false)}>
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="p-2">
            {!Array.isArray(notifications) || notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                No notifications
              </div>
            ) : (
              notifications.slice(0, 10).map((notification) => (
                <div
                  key={notification.id || Math.random()}
                  className={`p-3 mb-2 rounded-lg border ${
                    notification.read
                      ? 'bg-slate-800/50 border-slate-700'
                      : 'bg-cyan-900/20 border-cyan-500/30'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {getIcon(notification.type)}
                    <div className="flex-1">
                      <p className="font-bold text-white text-sm">{notification.title || 'Notification'}</p>
                      <p className="text-gray-400 text-xs mt-1">{notification.message || ''}</p>
                      <p className="text-gray-500 text-xs mt-2">
                        {notification.timestamp ? new Date(notification.timestamp?.toDate?.() || notification.timestamp).toLocaleString() : ''}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RealtimeNotifications;
